import { useState, useEffect, useRef, useCallback } from 'react';
import { User, Mail, Phone, MessageSquare, AlertCircle, ChevronLeft, ChevronRight, UserCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackCheckoutStarted, updateAbandonmentStep } from '@/lib/analytics';
import type { ExperienceDetail } from '@/lib/api';
import type { BookingData } from './BookingFlow';

interface GuestStepProps {
  experience: ExperienceDetail;
  bookingData: BookingData;
  onUpdate: (data: Partial<BookingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Format phone number as user types
 * Handles US format: (xxx) xxx-xxxx
 */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else {
    return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
  }
}

/**
 * Validate phone number (basic validation)
 */
function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10;
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(email);
}

/**
 * GuestStep - Premium guest information form
 * Features elegant form inputs with refined styling
 */
export function GuestStep({ experience, bookingData, onUpdate, onNext, onBack }: GuestStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const hasTracked = useRef(false);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Track checkout started when entering guest info step
  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      const totalGuests = bookingData.adultCount + bookingData.childCount;
      trackCheckoutStarted(experience.id, totalGuests);
      updateAbandonmentStep('checkout', experience.id);
    }
  }, [experience.id, bookingData.adultCount, bookingData.childCount]);

  // Focus first name field on mount
  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const handleChange = useCallback((field: keyof BookingData['guestInfo'], value: string) => {
    const formattedValue = field === 'phone' ? formatPhoneNumber(value) : value;
    
    onUpdate({
      guestInfo: { ...bookingData.guestInfo, [field]: formattedValue },
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [bookingData.guestInfo, errors, onUpdate]);

  const handleBlur = useCallback((field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    const newErrors: Record<string, string> = {};
    
    if (field === 'firstName' && !bookingData.guestInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (field === 'lastName' && !bookingData.guestInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (field === 'email') {
      if (!bookingData.guestInfo.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!isValidEmail(bookingData.guestInfo.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    if (field === 'phone') {
      if (!bookingData.guestInfo.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!isValidPhone(bookingData.guestInfo.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
  }, [bookingData.guestInfo]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!bookingData.guestInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!bookingData.guestInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!bookingData.guestInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(bookingData.guestInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!bookingData.guestInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(bookingData.guestInfo.phone)) {
      newErrors.phone = 'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    setTouched({ firstName: true, lastName: true, email: true, phone: true });
    
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
      errorElement?.focus();
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validate()) {
      onNext();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleContinue();
    }
  };

  // Input styling helper
  const getInputClassName = (fieldName: string, hasIcon: boolean = true) => cn(
    'input-luxury',
    hasIcon && 'pl-12',
    errors[fieldName] && touched[fieldName] && 'border-destructive focus:ring-destructive/20 focus:border-destructive'
  );

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <UserCircle className="h-5 w-5 text-primary" />
          </div>
          <h2 className="font-serif text-xl font-medium text-foreground">Guest Information</h2>
        </div>
        <p className="text-sm text-muted-foreground ml-13">
          Tell us who&apos;s coming so we can prepare for your arrival
        </p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleContinue(); }} className="space-y-5">
        {/* Name Fields */}
        <div className="grid gap-5 sm:grid-cols-2">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="mb-2 block text-sm font-semibold text-foreground">
              First Name <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center">
                <User className="h-4 w-4 text-muted-foreground/60" />
              </div>
              <input
                ref={firstNameRef}
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={bookingData.guestInfo.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                onKeyDown={handleKeyDown}
                placeholder="John"
                aria-invalid={!!errors.firstName}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                className={getInputClassName('firstName')}
              />
            </div>
            {errors.firstName && touched.firstName && (
              <p id="firstName-error" className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.firstName}
              </p>
            )}
          </div>
          
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="mb-2 block text-sm font-semibold text-foreground">
              Last Name <span className="text-destructive">*</span>
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={bookingData.guestInfo.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              onKeyDown={handleKeyDown}
              placeholder="Doe"
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              className={getInputClassName('lastName', false)}
            />
            {errors.lastName && touched.lastName && (
              <p id="lastName-error" className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.lastName}
              </p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-semibold text-foreground">
            Email <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center">
              <Mail className="h-4 w-4 text-muted-foreground/60" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={bookingData.guestInfo.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              onKeyDown={handleKeyDown}
              placeholder="john@example.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : 'email-hint'}
              className={getInputClassName('email')}
            />
          </div>
          {errors.email && touched.email ? (
            <p id="email-error" className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
              <AlertCircle className="h-3.5 w-3.5" />
              {errors.email}
            </p>
          ) : (
            <p id="email-hint" className="mt-2 text-xs text-muted-foreground">
              Confirmation will be sent to this email
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-foreground">
            Phone <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center">
              <Phone className="h-4 w-4 text-muted-foreground/60" />
            </div>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={bookingData.guestInfo.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              onKeyDown={handleKeyDown}
              placeholder="(555) 000-0000"
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'phone-error' : 'phone-hint'}
              className={getInputClassName('phone')}
            />
          </div>
          {errors.phone && touched.phone ? (
            <p id="phone-error" className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
              <AlertCircle className="h-3.5 w-3.5" />
              {errors.phone}
            </p>
          ) : (
            <p id="phone-hint" className="mt-2 text-xs text-muted-foreground">
              For booking updates and reminders
            </p>
          )}
        </div>

        {/* Special Requests */}
        <div>
          <label htmlFor="specialRequests" className="mb-2 block text-sm font-semibold text-foreground">
            Special Requests <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <div className="relative">
            <div className="absolute left-4 top-4 flex h-5 w-5 items-center justify-center">
              <MessageSquare className="h-4 w-4 text-muted-foreground/60" />
            </div>
            <textarea
              id="specialRequests"
              name="specialRequests"
              value={bookingData.specialRequests}
              onChange={(e) => onUpdate({ specialRequests: e.target.value })}
              placeholder="Any dietary requirements, accessibility needs, or other requests..."
              rows={3}
              className={cn(
                'input-luxury pl-12 resize-none',
                'min-h-[100px]'
              )}
            />
          </div>
        </div>

        {/* Privacy Note */}
        <div className="rounded-xl bg-muted/30 border border-border/30 p-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Your information is kept private and secure. We&apos;ll only use it to confirm your booking and send important updates about your experience.
          </p>
        </div>

        {/* Navigation */}
        <div className="pt-4 flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="btn-luxury-ghost"
          >
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
          <button
            type="submit"
            className="btn-luxury"
          >
            Continue
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
